import { inject, Injectable } from '@angular/core';
import { ApiService, GenericResponseCode, GenericResponseResult } from '@brighthub/api';
import {
  catchError,
  concat,
  defer,
  EMPTY,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import {
  ClientProfilePayload,
  ClientProfileResponse,
  ClientProfileSection,
  ClientProfileSectionsResponse,
} from './client-profile.model';

const missingGuidResult: GenericResponseResult['result'] = {
  status: 400,
  code: GenericResponseCode.MISSING_REQ_FIELD,
  message: 'GUID is required',
  suggestion: '',
  suggestionCopyKey: '',
  docs: '',
};

interface CachedMergedSections {
  sections: ClientProfileSection[];
  /** When true, all incremental sources after the primary client-info response have finished. */
  incrementalLoadComplete: boolean;
}

/**
 * Fetches client profile sections from the prototype API (and related incremental
 * endpoints), merges them in order, and caches per GUID so reopening the profile
 * avoids repeating requests.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientProfileSectionsService {
  private readonly _api = inject(ApiService);

  private readonly _cache = new Map<string, CachedMergedSections>();

  /** Sections from cache for immediate UI when revisiting the same client (no network). */
  peekSections(guid: string): ClientProfileSection[] | null {
    const trimmed = guid?.trim();
    if (!trimmed) {
      return null;
    }
    return this._cache.get(trimmed)?.sections ?? null;
  }

  /** Drop cached sections for a client (e.g. after a successful save). */
  invalidate(guid: string): void {
    const trimmed = guid?.trim();
    if (trimmed) {
      this._cache.delete(trimmed);
    }
  }

  /**
   * Loads profile data incrementally: GET `client-info`, then `household-info`, then
   * `employment-info`. Each step emits an updated merged `sections` list (append order).
   * After that, GET `documents` runs for diagnostics only (response is not merged
   * or surfaced in the UI).
   *
   * When `force` is false and a full cached payload exists (`incrementalLoadComplete`),
   * emits once from cache without network calls (including no `documents` request).
   */
  getProfileSections(
    guid: string,
    options?: { force?: boolean }
  ): Observable<ClientProfileResponse & GenericResponseResult> {
    const trimmed = guid?.trim() ?? '';
    if (!trimmed) {
      return of({
        result: missingGuidResult,
        payload: { sections: [] },
      });
    }

    if (!options?.force) {
      const hit = this._cache.get(trimmed);
      if (hit?.incrementalLoadComplete) {
        return of(this._okResponse({ sections: hit.sections }));
      }
    } else {
      this._cache.delete(trimmed);
    }

    return this._fetchProfileSections(trimmed).pipe(
      switchMap((res) => {
        const payload = res.payload;
        if (
          res.result.code !== GenericResponseCode.SERVICE_OK ||
          !payload ||
          !Array.isArray(payload.sections)
        ) {
          return of(res);
        }

        const primary = payload.sections;
        this._cache.set(trimmed, {
          sections: [...primary],
          incrementalLoadComplete: false,
        });

        const firstEmit = this._okResponse({ sections: [...primary] });

        return concat(
          of(firstEmit),
          this._fetchHouseholdMembers(trimmed).pipe(
            map((houseRes) => {
              const hPayload = houseRes.payload;
              const raw = hPayload?.sections;
              const extra =
                houseRes.result.code === GenericResponseCode.SERVICE_OK &&
                Array.isArray(raw)
                  ? raw
                  : [];
              const merged = [...primary, ...extra];
              this._cache.set(trimmed, {
                sections: merged,
                incrementalLoadComplete: false,
              });
              return this._okResponse({ sections: merged });
            })
          ),
          this._fetchEmploymentInfo(trimmed).pipe(
            map((empRes) => {
              const base = this._cache.get(trimmed)?.sections ?? [...primary];
              const ePayload = empRes.payload;
              const raw = ePayload?.sections;
              const extra =
                empRes.result.code === GenericResponseCode.SERVICE_OK &&
                Array.isArray(raw)
                  ? raw
                  : [];
              const merged = [...base, ...extra];
              this._cache.set(trimmed, {
                sections: merged,
                incrementalLoadComplete: true,
              });
              return this._okResponse({ sections: merged });
            })
          )
        );
      })
    );
  }

  private _fetchProfileSections(
    guid: string
  ): Observable<ClientProfileResponse & GenericResponseResult> {
    const path = `/v1/prototype/client/client-info?guid=${encodeURIComponent(guid)}`;
    return this._api
      .get<ClientProfileSectionsResponse>('brightnav-api', path)
      .pipe(
        catchError((err: unknown) => {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message?: string }).message)
              : 'Failed to load profile';
          return of({
            result: {
              status: 500,
              code: GenericResponseCode.SERVICE_ERROR,
              message,
              suggestion: '',
              suggestionCopyKey: '',
              docs: '',
            },
            payload: { sections: [] },
          });
        })
      );
  }

  private _fetchHouseholdMembers(
    guid: string
  ): Observable<ClientProfileResponse & GenericResponseResult> {
    const path = `/v1/prototype/client/household-info?guid=${encodeURIComponent(guid)}`;
    return this._api
      .get<ClientProfileSectionsResponse>('brightnav-api', path)
      .pipe(
        catchError((err: unknown) => {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message?: string }).message)
              : 'Failed to load household members';
          return of({
            result: {
              status: 500,
              code: GenericResponseCode.SERVICE_ERROR,
              message,
              suggestion: '',
              suggestionCopyKey: '',
              docs: '',
            },
            payload: { sections: [] },
          });
        })
      );
  }

  private _fetchEmploymentInfo(
    guid: string
  ): Observable<ClientProfileResponse & GenericResponseResult> {
    const path = `/v1/prototype/client/employment-info?guid=${encodeURIComponent(guid)}`;
    return this._api
      .get<ClientProfileSectionsResponse>('brightnav-api', path)
      .pipe(
        catchError((err: unknown) => {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message?: string }).message)
              : 'Failed to load employment info';
          return of({
            result: {
              status: 500,
              code: GenericResponseCode.SERVICE_ERROR,
              message,
              suggestion: '',
              suggestionCopyKey: '',
              docs: '',
            },
            payload: { sections: [] },
          });
        })
      );
  }

  /**
   * Fire-and-forget GET for DevTools inspection; does not emit to profile subscribers.
   */
  private _probeDocuments(guid: string): Observable<never> {
    const path = `/v1/prototype/client/documents?guid=${encodeURIComponent(guid)}`;
    return this._api.get<unknown>('brightnav-api', path).pipe(
      catchError(() => EMPTY),
      switchMap(() => EMPTY)
    );
  }

  private _okResponse(payload: ClientProfilePayload): ClientProfileResponse &
    GenericResponseResult {
    return {
      result: {
        status: 200,
        code: GenericResponseCode.SERVICE_OK,
        message: '',
        suggestion: '',
        suggestionCopyKey: '',
        docs: '',
      },
      payload,
    };
  }
}
